import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";
import { Checkbox, cn } from "@nexus/ui";
import { NEW_PARTITION_RULE, ONBOARDING_EDIT_COLUMNS, type OnboardingEditColumn } from "@nexus/contracts";
import type {
  NewOrgOnboardingIssue,
  OnboardingDraft,
  OnboardingDraftValue,
  OnboardingFieldErrors,
} from "@/features/identity-hub/lib/client-onboarding";
import type { CustomerSetupDraft, CustomerSetupErrors } from "@/features/identity-hub/lib/customer-setup";
import { onboardingSaveErrorBanner, type OnboardingSaveError } from "@/features/identity-hub/lib/onboarding-save-error";
import { ClientOnboardingFields, onboardingFieldLabel } from "../onboarding/ClientOnboardingFields";
import { CustomerSetupFields } from "../onboarding/CustomerSetupFields";
import { FieldHelp } from "../onboarding/FieldHelp";
import { OnboardingSection } from "../onboarding/OnboardingSection";
import { NewOrgSectionHeading } from "./NewOrgCreateAside";

const INPUT_CLASS =
  "h-9 w-full rounded-md border bg-background px-3 text-[13px] text-foreground outline-none placeholder:text-muted-foreground focus:border-ring focus:ring-1 focus:ring-ring/40";

interface NewOrgOnboardingStepProps {
  customerPartition: string;
  onCustomerPartitionChange: (value: string) => void;
  compKey: string;
  onCompKeyChange: (value: string) => void;
  compError: string | null;
  clientIndustry: string;
  onClientIndustryChange: (value: string) => void;
  draft: OnboardingDraft;
  onDraftChange: (column: OnboardingEditColumn, value: OnboardingDraftValue) => void;
  errors: OnboardingFieldErrors;
  setupDraft: CustomerSetupDraft;
  onSetupDraftChange: (draft: CustomerSetupDraft) => void;
  setupErrors: CustomerSetupErrors;
  issue: NewOrgOnboardingIssue | null;
  /** Errors stay hidden until the user tries to create the organization. */
  showErrors: boolean;
  /** Why the last onboarding create was not saved; the form is kept for a resubmit. */
  saveError: OnboardingSaveError | null;
  /** Set once the organization exists but its onboarding is not saved yet. */
  createdOrganizationName: string | null;
  /** Opt-in, off by default: unchecked creates only the Nexus organization and sends no onboarding. */
  isIncluded: boolean;
  onIncludedChange: (isIncluded: boolean) => void;
}

const NO_ERRORS = {};
const INLINE_ERROR_FIELDS: readonly string[] = ["industry", "compKey", ...ONBOARDING_EDIT_COLUMNS];

/**
 * Optional onboarding step. The customer partition is saved with the tenant;
 * only when the user opts in are Client Industry, the onboarding fields and the
 * customer setup sent to the client-context API, and Nexus keeps the onboarding
 * once it is accepted. Left unchecked, only the Nexus organization is created.
 */
export function NewOrgOnboardingStep({
  customerPartition,
  onCustomerPartitionChange,
  compKey,
  onCompKeyChange,
  compError,
  clientIndustry,
  onClientIndustryChange,
  draft,
  onDraftChange,
  errors,
  setupDraft,
  onSetupDraftChange,
  setupErrors,
  issue,
  showErrors,
  saveError,
  createdOrganizationName,
  isIncluded,
  onIncludedChange,
}: NewOrgOnboardingStepProps) {
  const { t } = useTranslation();
  const saveErrorBanner = onboardingSaveErrorBanner(saveError, INLINE_ERROR_FIELDS);
  const partitionLabel = t("iam.orgDrawer.settings.customerPartitionLabel", "Customer partition");
  const compKeyLabel = t("iam.newOrgDialog.onboarding.compKeyLabel", "Competitor partition");
  const industryLabel = onboardingFieldLabel(t, "industry");
  const visibleIssue = showErrors ? issue : null;
  const partitionError =
    visibleIssue === "partitionRequired"
      ? t("iam.onboarding.errors.partitionRequired", "Customer data needs a customer partition")
      : visibleIssue === "partitionMultiple"
        ? t("iam.onboarding.errors.partitionMultiple", "Enter exactly one customer name")
        : visibleIssue === "partitionInvalid"
          ? t("iam.onboarding.errors.partitionInvalid", NEW_PARTITION_RULE)
          : null;
  const industryError =
    visibleIssue === "industryRequired"
      ? t("iam.onboarding.industryRequired", "Client industry is required to create onboarding")
      : visibleIssue === "industrySpaces"
        ? t("iam.onboarding.industrySpaces", "Do not use spaces")
        : saveError?.field === "industry"
          ? saveError.message
          : null;

  return (
    <div className="flex flex-col gap-2">
      {createdOrganizationName !== null && (
        <div
          className="flex flex-col gap-1 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3"
          role="alert"
          data-testid="new-org-onboarding-failed"
        >
          <p className="text-sm text-foreground">
            {t("iam.newOrgDialog.onboarding.notSaved", {
              name: createdOrganizationName,
              defaultValue: "{{name}} was created, but its customer data could not be saved. Please try again.",
            })}
          </p>
          {saveErrorBanner && !saveError?.isGeneral && <p className="text-sm text-destructive">{saveErrorBanner}</p>}
        </div>
      )}
      <section className="flex flex-col gap-4">
        <NewOrgSectionHeading
          title={t("iam.newOrgDialog.onboarding.title", "Customer data")}
          hint={t("iam.newOrgDialog.onboarding.hint", "Optional. Set it up now, or later from Edit organization.")}
        />
        {createdOrganizationName === null && (
          <label
            className={cn(
              "flex items-start gap-2 rounded-lg border p-3 text-xs text-foreground",
              isIncluded ? "border-primary/40 bg-primary/5" : "border-border",
            )}
            data-testid="new-org-include-onboarding"
          >
            <Checkbox
              checked={isIncluded}
              onCheckedChange={(checked) => onIncludedChange(checked === true)}
              aria-label={t("iam.newOrgDialog.onboarding.includeLabel", "Set up customer data")}
            />
            <span className="flex flex-col gap-0.5">
              <span className="font-medium">{t("iam.newOrgDialog.onboarding.includeLabel", "Set up customer data")}</span>
              <span className="text-muted-foreground">
                {t(
                  "iam.newOrgDialog.onboarding.includeHint",
                  "Also creates this customer's onboarding records: the brand context and data setup that Brand Brain, insights and opportunities use. Leave it off to create the organization in Nexus only.",
                )}
              </span>
            </span>
          </label>
        )}
      </section>
      <OnboardingSection
        className="px-0"
        data-testid="new-org-onboarding-customer"
        title={t("iam.onboarding.identityTitle", "Customer")}
        hint={
          isIncluded
            ? t("iam.newOrgDialog.onboarding.requiredLegend", "Fields marked * are required.")
            : undefined
        }
      >
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex items-center gap-1.5">
            <label htmlFor="new-org-customer-partition" className="text-xs font-medium text-foreground">
              {partitionLabel}{" "}
              {isIncluded ? (
                <span className="text-destructive">*</span>
              ) : (
                <span className="font-normal text-muted-foreground">{t("iam.newOrgDialog.defaultBrandOptional", "(optional)")}</span>
              )}
            </label>
            {isIncluded && (
              <FieldHelp label={partitionLabel}>
                {t(
                  "iam.newOrgDialog.onboarding.partitionHelp",
                  "Example: acme-corp. It links this organization to its customer data.",
                )}
              </FieldHelp>
            )}
          </div>
          <input
            id="new-org-customer-partition"
            type="text"
            placeholder={t("iam.newOrgDialog.onboarding.partitionPlaceholder", "e.g. acme-corp")}
            required={isIncluded}
            value={customerPartition}
            onChange={(e) => onCustomerPartitionChange(e.target.value)}
            disabled={createdOrganizationName !== null}
            aria-invalid={partitionError ? true : undefined}
            aria-describedby="new-org-customer-partition-hint"
            className={cn(INPUT_CLASS, partitionError ? "border-destructive" : "border-input-border")}
          />
          <p id="new-org-customer-partition-hint" className={cn("text-[11px]", partitionError ? "text-destructive" : "text-muted-foreground")}>
            {partitionError ??
              (isIncluded
                ? t("iam.newOrgDialog.onboarding.partitionHint", "The name this customer's data is stored under.")
                : t("iam.newOrgDialog.onboarding.partitionSkippedHint", "Saved on the organization only."))}
          </p>
        </div>
        {isIncluded && (
          <>
            <div className="flex min-w-0 flex-col gap-1.5">
              <div className="flex items-center gap-1.5">
                <label htmlFor="new-org-comp-key" className="text-xs font-medium text-foreground">
                  {compKeyLabel} <span className="text-destructive">*</span>
                </label>
                <FieldHelp label={compKeyLabel}>
                  {t(
                    "iam.newOrgDialog.onboarding.compKeyHelp",
                    "Change it only when this customer maps to a different competitor.",
                  )}
                </FieldHelp>
              </div>
              <input
                id="new-org-comp-key"
                type="text"
                required
                value={compKey}
                onChange={(e) => onCompKeyChange(e.target.value)}
                aria-invalid={compError ? true : undefined}
                aria-describedby="new-org-comp-key-hint"
                className={cn(INPUT_CLASS, compError ? "border-destructive" : "border-input-border")}
              />
              <p id="new-org-comp-key-hint" className={cn("text-[11px]", compError ? "text-destructive" : "text-muted-foreground")}>
                {compError ??
                  t("iam.newOrgDialog.onboarding.compKeyHint", "Defaults to the customer partition.")}
              </p>
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <div className="flex items-center gap-1.5">
                <label htmlFor="new-org-client-industry" className="flex items-center gap-1 text-xs font-medium text-foreground">
                  <Lock className="size-3 text-muted-foreground" aria-hidden />
                  {industryLabel} <span className="text-destructive">*</span>
                </label>
                <FieldHelp label={industryLabel}>
                  {t("iam.onboarding.industryHelp", "Example: SkinCare. Separate from the Comp industries on Data access.")}
                </FieldHelp>
              </div>
              <input
                id="new-org-client-industry"
                type="text"
                required
                value={clientIndustry}
                onChange={(e) => onClientIndustryChange(e.target.value)}
                aria-invalid={industryError ? true : undefined}
                aria-describedby="new-org-client-industry-hint"
                className={cn(INPUT_CLASS, industryError ? "border-destructive" : "border-input-border")}
              />
              <p id="new-org-client-industry-hint" className={cn("text-[11px]", industryError ? "text-destructive" : "text-muted-foreground")}>
                {industryError ??
                  t("iam.onboarding.industryCreateHint", "One word, no spaces. Can't be changed later.")}
              </p>
            </div>
          </>
        )}
      </OnboardingSection>
      {isIncluded && (
        <>
          <CustomerSetupFields
            sectionClassName="px-0"
            draft={setupDraft}
            errors={showErrors ? setupErrors : NO_ERRORS}
            onChange={onSetupDraftChange}
          />
          <ClientOnboardingFields
            sectionClassName="px-0"
            draft={draft}
            errors={showErrors ? errors : NO_ERRORS}
            saveError={saveError}
            onChange={onDraftChange}
          />
        </>
      )}
    </div>
  );
}
